import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useState } from 'react'
import { SimpleWorkspace } from '@opallabs/concepts'
import { MessageKeys, useMessage, useDebouncedValue } from '@opallabs/hooks'
import { SearchResult } from '../types'
import { searchQueryFunction } from './searchQueryFunction'

const SEARCH_DELAY_MS = 150
const MIN_SEARCH_LENGTH = 3

const SearchQueryKey = '/api/v1/search'
export type JumpSearchContextValue = {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  clearSearchResults: () => void
  searchResults?: SearchResult[]
  searchTerm: string
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>
  workspaceSlug?: string
}

export const JumpSearchContext = createContext<
  JumpSearchContextValue | undefined
>(undefined)
JumpSearchContext.displayName = 'JumpSearchContext'

const sanitizeTerm = (term: string) => term.replace(/[%#]/g, '')

export const JumpSearchProvider: React.FC<
  React.PropsWithChildren<{
    workspace: SimpleWorkspace | null
  }>
> = ({ children, workspace }) => {
  const [open, setOpen] = useState(false)

  useMessage(MessageKeys.openJumpSearch, () => {
    if (!open) setOpen(true)
  })

  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebouncedValue(searchTerm, SEARCH_DELAY_MS)

  const enableSearchQuery =
    workspace !== null && debouncedSearchTerm.length >= MIN_SEARCH_LENGTH

  const result = useQuery({
    queryKey: [
      SearchQueryKey,
      {
        searchTerm: sanitizeTerm(debouncedSearchTerm),
        workspaceSlug: workspace?.slug,
        workspaceId: workspace?.id,
      },
    ],
    queryFn: searchQueryFunction,
    enabled: enableSearchQuery,
    keepPreviousData: true,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const clearSearchResults = useCallback(() => {
    queryClient.setQueryData([SearchQueryKey], [])
  }, [queryClient])

  return (
    <JumpSearchContext.Provider
      value={{
        clearSearchResults,
        open,
        searchResults: result.data,
        searchTerm,
        setOpen,
        setSearchTerm,
        workspaceSlug: workspace?.slug,
      }}
    >
      {children}
    </JumpSearchContext.Provider>
  )
}

export const useJumpSearch = (): JumpSearchContextValue => {
  const context = useContext(JumpSearchContext)

  if (context === undefined) {
    throw new Error('useJumpSearch must exist inside of an JumpSearchContext.')
  }

  return context
}
